"use client";

/**
 * Blog Analytics Hook
 *
 * Tracks user engagement on blog posts:
 * - Scroll depth (percentage of page scrolled)
 * - Time on page (seconds spent reading)
 * - Sends data to the CMS API for analytics
 */

import { useEffect, useRef, useCallback } from "react";
import { getSessionId } from "@/lib/session";

const API_BASE = process.env.NEXT_PUBLIC_WEBSITE_JOBS_API;

interface UseBlogAnalyticsOptions {
  /**
   * Minimum time (ms) before first tracking event
   * @default 3000
   */
  minTimeBeforeTrack?: number;

  /**
   * Interval (ms) between tracking updates
   * @default 10000
   */
  trackingInterval?: number;

  /**
   * Whether tracking is enabled
   * @default true
   */
  enabled?: boolean;
}

export function useBlogAnalytics(
  slug: string,
  options: UseBlogAnalyticsOptions = {}
) {
  const {
    minTimeBeforeTrack = 3000,
    trackingInterval = 10000,
    enabled = true,
  } = options;

  const startTime = useRef(Date.now());
  const maxScrollDepth = useRef(0);
  const hasTracked = useRef(false);
  const lastTrackTime = useRef(0);

  const sendTrackingData = useCallback(async () => {
    if (!API_BASE || !enabled) return;

    // Debounce tracking calls
    const now = Date.now();
    if (now - lastTrackTime.current < 2000) return;
    lastTrackTime.current = now;

    const sessionId = getSessionId();
    if (!sessionId) return;

    const timeOnPage = Math.round((Date.now() - startTime.current) / 1000);

    try {
      await fetch(`${API_BASE}/blogs/${slug}/view/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          scroll_depth: maxScrollDepth.current,
          time_on_page: timeOnPage,
          referrer: typeof document !== "undefined" ? document.referrer : "",
        }),
      });
      hasTracked.current = true;
    } catch (error) {
      console.error("Failed to track blog view:", error);
    }
  }, [slug, enabled]);

  useEffect(() => {
    if (!enabled || !API_BASE) return;

    // Track scroll depth
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        const scrollPercent = Math.round((scrollTop / docHeight) * 100);
        maxScrollDepth.current = Math.max(
          maxScrollDepth.current,
          Math.min(scrollPercent, 100)
        );
      }
    };

    // Initial tracking after minimum time
    const initialTrackTimeout = setTimeout(() => {
      sendTrackingData();
    }, minTimeBeforeTrack);

    // Periodic tracking updates
    const trackingIntervalId = setInterval(() => {
      sendTrackingData();
    }, trackingInterval);

    // Track on page visibility change (user switching tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        sendTrackingData();
      }
    };

    // Track before page unload
    const handleBeforeUnload = () => {
      sendTrackingData();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Initial scroll position
    handleScroll();

    return () => {
      clearTimeout(initialTrackTimeout);
      clearInterval(trackingIntervalId);
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);

      // Final tracking on unmount
      sendTrackingData();
    };
  }, [slug, enabled, minTimeBeforeTrack, trackingInterval, sendTrackingData]);

  return {
    scrollDepth: maxScrollDepth.current,
    hasTracked: hasTracked.current,
  };
}
